/**
 * CMS catalog review queue (CMS-15, docs/06 §2.15, docs/03 §2 "CMS", WP3.5).
 *
 * `tools/catalog-identify` (WP0.7) leaves a `review` bucket — weak matches, swap suspects, ISRC
 * conflicts — and `tools/catalog-clean` (WP0.8) leaves the swap suspects it could not confirm and
 * the artists it tagged `placeholder`. Together they are the ~1 % of the catalog that needs eyes;
 * `review-queue` loads them into D1 `catalog_review` (migration 0005) and this screen resolves them.
 */
import { z } from 'zod';

import { EntityId, IsoDate, Page, PageQuery } from '../primitives';
import { Timestamps } from './common';
import { CmsSongDTO } from './songs';

/** Why the row is in the queue. */
export const ReviewKinds = ['swap', 'match', 'placeholder'] as const;
export const ReviewKind = z.enum(ReviewKinds);
export type ReviewKind = z.infer<typeof ReviewKind>;

/**
 * `open` until an operator acts: `accepted` (the candidate ids/cover were written to the song),
 * `swapped` (title and artist exchanged), `no_artist` (the credited artist is a placeholder,
 * not an act) or `ignored` (nothing to do).
 */
export const ReviewStatuses = ['open', 'accepted', 'swapped', 'no_artist', 'ignored'] as const;
export const ReviewStatus = z.enum(ReviewStatuses);
export type ReviewStatus = z.infer<typeof ReviewStatus>;

/**
 * One provider candidate as the loader captured it. Every field is present and nullable so the
 * card of CMS-15 can render "—" without guessing, and so a stored payload round-trips unchanged.
 */
export const ReviewCandidate = z.object({
  title: z.string().nullable(),
  artist: z.string().nullable(),
  /** 0..1 similarity the resolver gave this candidate. */
  score: z.number().nullable(),
  /** MusicBrainz recording MBID (MusicBrainz candidates only). */
  mbid: z.string().nullable(),
  artistMbids: z.array(z.string()),
  isrc: z.string().nullable(),
  /** Deezer track id (Deezer candidates only). */
  deezerId: z.number().int().nullable(),
  coverUrl: z.string().nullable(),
});
export type ReviewCandidate = z.infer<typeof ReviewCandidate>;

/** The client's Excel row exactly as delivered — the left pane of the card. */
export const ReviewExcelRow = z.object({
  sourceId: z.number().int().nullable(),
  title: z.string(),
  artist: z.string(),
  language: z.string().nullable(),
  genre: z.string().nullable(),
});
export type ReviewExcelRow = z.infer<typeof ReviewExcelRow>;

/** What `catalog_review.payload_json` holds. */
export const ReviewPayload = z.object({
  excel: ReviewExcelRow,
  mb: ReviewCandidate.nullable(),
  deezer: ReviewCandidate.nullable(),
  /** Best cover URL of the two candidates, hoisted for the "Aceptar" action. */
  coverUrl: z.string().nullable(),
  /** One line of evidence, shown next to the score chip ("las columnas parecen intercambiadas"). */
  reason: z.string().nullable(),
  /** Resolver flags (`swap_suspected`, `weak_artist`, `isrc_conflict`, …). */
  flags: z.array(z.string()),
  /** Title/artists the `swap` action should write; the API falls back to exchanging the row. */
  swap: z.object({ title: z.string(), artists: z.array(z.string()) }).nullable(),
});
export type ReviewPayload = z.infer<typeof ReviewPayload>;

/** The song the row points at, as the card header shows it. */
export const ReviewSongRef = z.object({
  id: EntityId,
  title: z.string(),
  artists: z.array(z.object({ id: EntityId, name: z.string() })),
  sourceId: z.number().int().nullable(),
  /** Our R2 cover, never the candidate's `coverUrl` (Deezer's CDN) — QA3 CMS-64. */
  imageUrl: z.string().nullable(),
  matchStatus: z.string().nullable(),
});
export type ReviewSongRef = z.infer<typeof ReviewSongRef>;

export const CmsReviewDTO = z
  .object({
    id: EntityId,
    songId: EntityId,
    kind: ReviewKind,
    /** 0..1 confidence of the evidence. */
    score: z.number().nullable(),
    status: ReviewStatus,
    payload: ReviewPayload,
    resolvedBy: z.string().nullable(),
    resolvedAt: IsoDate.nullable(),
    song: ReviewSongRef,
  })
  .merge(Timestamps);
export type CmsReviewDTO = z.infer<typeof CmsReviewDTO>;

/** Tab counters: open rows per kind for the current status filter (docs/06 §2.15). */
export const CmsReviewCounts = z.object({
  swap: z.number().int().nonnegative(),
  match: z.number().int().nonnegative(),
  placeholder: z.number().int().nonnegative(),
  /** The three above added up — "N filas por revisar". */
  total: z.number().int().nonnegative(),
  /** Live songs in the catalog — "de 143.392". */
  catalogTotal: z.number().int().nonnegative(),
});
export type CmsReviewCounts = z.infer<typeof CmsReviewCounts>;

/** `sort` values `GET /cms/review` accepts; the screen defaults to the lowest score first. */
export const ReviewSorts = ['score', 'created_at', 'source_id'] as const;
export const ReviewSort = z.enum(ReviewSorts);
export type ReviewSort = z.infer<typeof ReviewSort>;

/** `GET /cms/review?kind=&status=&sort=score` (server-side filtering, docs/06 §4). */
export const CmsReviewQuery = PageQuery.extend({
  kind: ReviewKind.optional(),
  /** `all` lifts the filter; the default queue is the open rows. */
  status: z.enum([...ReviewStatuses, 'all']).default('open'),
  sort: ReviewSort.default('score'),
});
export type CmsReviewQuery = z.infer<typeof CmsReviewQuery>;
export type CmsReviewQueryInput = z.input<typeof CmsReviewQuery>;

export const CmsReviewResponse = Page(CmsReviewDTO).extend({ counts: CmsReviewCounts });
export type CmsReviewResponse = z.infer<typeof CmsReviewResponse>;

/**
 * Answer of the four resolve actions. `song` is the CMS song DTO after the write, so the screen
 * can show the new title/credit without re-fetching (`CmsSongDTO` lives in ./songs).
 */
export const CmsReviewActionResponse = z.object({
  item: CmsReviewDTO,
  song: CmsSongDTO,
});
export type CmsReviewActionResponse = z.infer<typeof CmsReviewActionResponse>;

/** `POST /cms/review/:id/{accept,swap,no-artist,ignore}` take no body. */
export const CmsReviewActions = ['accept', 'swap', 'no-artist', 'ignore'] as const;
export type CmsReviewAction = (typeof CmsReviewActions)[number];
